import { forwardRef, ReactNode } from 'react';
import classNames from 'classnames';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

import { Box } from '../Box/Box';
import { Text } from '../Text/Text';

import { tagClearButtonRecipe, tagRecipe, truncatedTextStyle } from './Tag.css';

type TagSize = 'small' | 'medium' | 'large';
type TagTone = 'success' | 'warning' | 'critical' | 'info' | 'neutral' | 'neutralInverted';
type TagVariant = 'solid' | 'outline';

export type TagProps = {
  /** Size of the Tag */
  size?: TagSize;
  /** Tone (color) of the Tag */
  tone?: TagTone;
  /** Variant (structure) of the Tag, i.e. 'solid' background or 'outline' */
  variant?: TagVariant;
  /** icon that precedes all other content in tag */
  leadingIcon?: ReactNode;
  children: string | ReactNode;
  /** icon that trails all other content in tag */
  trailingIcon?: ReactNode;
  disabled?: boolean;
  /** customize the styling of the tag container */
  className?: string;
  /** If `onClear` callback is passed, an `x` close button is rendered in the tag.
   *
   * The `onClear` callback is called when this `x` close button is clicked.
   */
  onClear?: () => void;
};

export const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    {
      size = 'medium',
      tone = 'info',
      variant = 'solid',
      leadingIcon,
      children,
      trailingIcon = null,
      disabled,
      className,
      onClear,
    },
    ref
  ) => {
    return (
      <Box
        className={classNames(
          tagRecipe({
            size,
            tone,
            variant,
            disabled,
          }),
          className
        )}
        ref={ref}
      >
        {leadingIcon && leadingIcon}
        <Text
          tone={tone === 'neutral' && variant === 'solid' ? 'neutralInverted' : 'neutral'}
          size={size}
          weight="demibold"
          className={truncatedTextStyle}
          untrimmed
          title={typeof children === 'string' ? children : undefined}
        >
          {children}
        </Text>
        {onClear ? (
          <Box
            component="button"
            onClick={onClear}
            className={tagClearButtonRecipe({
              size,
              variant,
            })}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Box>
        ) : (
          trailingIcon
        )}
      </Box>
    );
  }
);

Tag.displayName = 'Tag';
